import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLocale } from '../../application/i18n/LocaleProvider';
import { readBrowserLanguages } from '../../application/i18n/localePreference';
import { entryRoutePath } from '../../application/pwa/entryRoute';
import { countVisit } from '../../application/pwa/offerPreferences';
import { LANGUAGES, findLanguage, resolveLocaleForLanguage } from '../../domain/i18n/languages';
import { isPlantRoutePath, isSharedArrival, offeredLanguage } from '../../domain/plants/sharedArrival';
import { CloseIcon } from '../icons/ControlIcons';
import { LeafGlyph } from '../chrome/BrandIcons';

/**
 * What somebody sees when the first screen they have ever seen of this product
 * is one plant (SPEC-019 D-7).
 *
 * That is what a shared link produces, and until now it produced a page written
 * for somebody who already knew what this was: a plant, its needs, its care, and
 * no answer at all to "what is this site". The band answers it in one line,
 * offers the way further in, and gets out of the way.
 *
 * Deliberately NOT a dialog. Nothing here is worth blocking a reader's way to
 * the plant they were sent — the same call SPEC-016 D-5 made about the offers,
 * where the element choice IS the non-blocking guarantee.
 */

/**
 * Once per app start, not once per plant.
 *
 * Module scope rather than storage, and the distinction is the point: a visitor
 * who has swiped on to a second plant is no longer arriving, and one who comes
 * back tomorrow on a different link is arriving again. `browseState.js` keeps
 * its scroll offsets here for the same reason.
 */
let spent = false;

export default function SharedArrivalBand({ plantId }) {
    const { t, localeCode, languageCode } = useLocale();
    // Decided once, on mount. A visit counter that ticked while this was on
    // screen must not make the band vanish under the reader mid-sentence.
    const [isShowing, setShowing] = useState(() => {
        if (spent) {
            return false;
        }

        // `countVisit`, not `readVisitCount`: this band renders during the
        // first render of the first screen, and on a first-ever visit the
        // counter has not been written yet — `useAppOffers` writes it from an
        // effect, which is strictly later. `countVisit` is idempotent within a
        // page lifetime, so whichever of the two asks first does the counting
        // and both see the same number.
        spent = isSharedArrival({
            visits: countVisit(),
            // The address the APP started on, not this one. Walking from the
            // landing screen to a plant is not arriving on one.
            routeIsPlant: isPlantRoutePath(entryRoutePath()),
        });

        return spent;
    });

    if (!isShowing) {
        return null;
    }

    const offered = offeredLanguage({
        pageLanguage: languageCode,
        browserLanguages: readBrowserLanguages(),
        supported: LANGUAGES.map((language) => language.code),
    });
    const offeredLocale = offered ? resolveLocaleForLanguage(localeCode, offered) : null;

    return (
        <section className="shared-arrival" aria-label={t('plants.arrival.welcome')}>
            <div className="shared-arrival__head">
                <p className="shared-arrival__welcome">
                    <LeafGlyph size={22} />
                    {t('plants.arrival.welcome')}
                </p>
                <button
                    type="button"
                    className="shared-arrival__dismiss"
                    onClick={() => setShowing(false)}
                    aria-label={t('plants.arrival.dismiss')}
                >
                    <CloseIcon size={20} />
                </button>
            </div>
            <p className="shared-arrival__body">{t('plants.arrival.body')}</p>

            <div className="shared-arrival__actions">
                <Link className="shared-arrival__action" to={`/${localeCode}/plants`}>
                    {t('home.cta.discover')}
                </Link>
                {/* The same plant, in their language — not the catalogue.
                    Somebody was sent THIS plant, and a language switch that
                    landed them on a list would lose it.

                    Offered, never obeyed: the person who shared this chose the
                    language the link carries, and redirecting away from it would
                    throw away the one thing the shared page was for (D-7). */}
                {offeredLocale ? (
                    <Link
                        className="shared-arrival__language"
                        to={`/${offeredLocale}/plants/${plantId}`}
                        lang={offered}
                    >
                        {t('plants.arrival.language', { language: findLanguage(offered).endonym })}
                    </Link>
                ) : null}
            </div>
        </section>
    );
}
